import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { useState, useEffect, useRef } from "react";
import { apiService, type Lesson } from "../services/api.service";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { ChevronDown, X, Search } from "lucide-react";

interface LessonDropdownProps {
  value: Lesson | null;
  onChange: (lesson: Lesson | null) => void;
  userId?: string;
}

export const LessonDropdown = ({
  value,
  onChange,
  userId,
}: LessonDropdownProps) => {
  const [filters, setFilters] = useState<FilterValues>({});
  const [options, setOptions] = useState<{ organizations: { id: string; name: string }[]; teachers: { id: number; name: string }[] }>({ organizations: [], teachers: [] });
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch("/api/internal/admin/organizations", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : []),
      fetch("/api/proxy/lessons/search-options", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : { teachers: [] }),
    ]).then(([organizations, result]) => setOptions({ organizations, teachers: result.teachers })).catch(() => {});
    return () => controller.abort();
  }, []);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPublishedOnly, setShowPublishedOnly] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Invalidate in-flight responses as soon as any input changes, including
  // during the debounce window. Every request keeps the current search text.
  useEffect(() => {
    let current = true;
    if (!userId) {
      setLessons([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const data = await apiService.getLessons(searchTerm || undefined, userId, {
          organizationId: filters.organization_id || undefined,
          teacherId: filters.teacher_id ? Number(filters.teacher_id) : undefined,
          status: showPublishedOnly ? "published" : undefined,
        });
        if (current) setLessons(data);
      } catch (err) {
        if (!current) return;
        console.error("Failed to fetch lessons:", err);
        setLessons([]);
      } finally {
        if (current) setIsLoading(false);
      }
    }, 300);
    return () => {
      current = false;
      clearTimeout(timeout);
    };
  }, [searchTerm, userId, filters.organization_id, filters.teacher_id, showPublishedOnly]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (lesson: Lesson) => {
    onChange(lesson);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm("");
  };

  const filteredLessons = lessons.filter(
    (l) => !showPublishedOnly || l.is_published,
  );

  return (
    <div className="relative flex flex-col gap-1" ref={dropdownRef}>
      <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Lesson
      </label>
      <AdvancedOptions values={filters} onChange={setFilters} fields={[
        { key: "organization_id", label: "Organization", options: options.organizations.map((org) => ({ value: org.id, label: org.name })) },
        { key: "teacher_id", label: "Teacher owner", options: options.teachers.map((teacher) => ({ value: String(teacher.id), label: `${teacher.name} (#${teacher.id})` })) },
      ]} />
      <div
        className={`flex min-w-[200px] cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm transition-colors hover:border-muted-foreground/50 ${
          isOpen ? "border-primary" : "border-input"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "text-foreground" : "text-muted-foreground"}>
          {value ? value.name : "All lessons"}
        </span>
        {value ? (
          <button
            className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted-foreground/20 hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border bg-popover shadow-md">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search lessons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                className="w-full rounded border border-input bg-background py-2 pl-8 pr-3 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 border-b bg-muted/30 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50">
            <input
              type="checkbox"
              checked={showPublishedOnly}
              onChange={(e) => setShowPublishedOnly(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-primary"
            />
            <span>Published only</span>
          </label>
          <ScrollArea className="max-h-[250px]">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : filteredLessons.length > 0 ? (
              filteredLessons.map((lesson) => (
                <div
                  key={lesson.lesson_id}
                  className={`flex cursor-pointer flex-col gap-0.5 px-3 py-2.5 transition-colors hover:bg-muted/50 ${
                    value?.lesson_id === lesson.lesson_id ? "bg-primary/10" : ""
                  } ${!lesson.is_published ? "opacity-75" : ""}`}
                  onClick={() => handleSelect(lesson)}
                >
                  <span
                    className={`text-sm ${!lesson.is_published ? "text-muted-foreground" : "text-foreground"}`}
                  >
                    {lesson.name}
                    {!lesson.is_published && (
                      <Badge
                        variant="outline"
                        className="ml-2 bg-yellow-50 px-1.5 py-0 text-[0.65rem] font-semibold uppercase text-yellow-700"
                      >
                        Draft
                      </Badge>
                    )}
                  </span>
                  {lesson.teacher_name && (
                    <span className="text-xs text-muted-foreground">
                      {lesson.teacher_name}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No lessons found
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
